import {keysStore as platformUserKeysStore} from "@mytour/platform";
import {serviceDescriptionKeysStore} from "@mytour/service-catalog";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {companySettingsApiService} from "./companySettingsApi.service.js";
import {einvoicingKeysStore} from "./einvoicingQueries.js";
import {rdvActivationKeysStore} from "./rdvActivationQueries.js";

// ==================== Billing ====================

export function useUpdateTravelFees() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateTravelFees,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateTravelFees: mutateAsync, isUpdatingTravelFees: isPending};
}

export function useUpdateLegalIdentity() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateLegalIdentity,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateLegalIdentity: mutateAsync, isUpdatingLegalIdentity: isPending};
}

export function useUpdateLegalHeadquarters() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateLegalHeadquarters,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateLegalHeadquarters: mutateAsync, isUpdatingLegalHeadquarters: isPending};
}

export function useUpdateLegalNumbers() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateLegalNumbers,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
      // Setting/clearing the `vatId` triggers a backend backfill of
      // `service_description.vat_rate` (see `applyVatIdChangeToServiceDescriptions`
      // in `invoicingSetting.service.ts`). Refresh the catalog cache so previews
      // built from `useServiceDescriptions` immediately reflect the new VAT regime.
      queryClient.invalidateQueries({queryKey: serviceDescriptionKeysStore.root});
      // Le SIRET écrit ici pilote `blockers.missingSiret` (GET /api/einvoicing/) — même
      // pattern d'invalidation que `useCreatePennylaneAccount` pour une écriture de SIRET
      // (`einvoicingQueries.ts`).
      queryClient.invalidateQueries({queryKey: einvoicingKeysStore.status()});
    },
  });
  return {updateLegalNumbers: mutateAsync, isUpdatingLegalNumbers: isPending};
}

export function useUpdateInvoiceAppearance() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateInvoiceAppearance,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateInvoiceAppearance: mutateAsync, isUpdatingInvoiceAppearance: isPending};
}

export function useUpdateInvoiceParameters() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateInvoiceParameters,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateInvoiceParameters: mutateAsync, isUpdatingInvoiceParameters: isPending};
}

// ==================== My Business ====================

export function useUpdateBusinessDescriptor() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateBusinessDescriptor,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
      queryClient.invalidateQueries({queryKey: ["customer"]});
    },
  });
  return {updateBusinessDescriptor: mutateAsync, isUpdatingBusinessDescriptor: isPending};
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateOrganization,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
      // Working hours are an RDV activation prerequisite (Story 5.1, #607):
      // refresh the activation status so the switch/missing list reflects the
      // edit without waiting for a remount.
      queryClient.invalidateQueries({queryKey: rdvActivationKeysStore.status()});
    },
  });
  return {updateOrganization: mutateAsync, isUpdatingOrganization: isPending};
}

// ==================== Notifications ====================

export function useUpdateNotifications() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: companySettingsApiService.updateNotifications,
    onSuccess() {
      queryClient.invalidateQueries({queryKey: platformUserKeysStore.root});
    },
  });
  return {updateNotifications: mutateAsync, isUpdatingNotifications: isPending};
}

// ==================== SMS ====================

export const smsKeysStore = {
  root: ["smsStatus"] as const,
};

export function useSmsStatus() {
  const {data, isLoading} = useQuery({
    queryKey: smsKeysStore.root,
    queryFn: companySettingsApiService.getSmsStatus,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    smsStatus: data,
    isSmsStatusLoading: isLoading,
  };
}
